import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarHeart, Loader2, Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => format(d, "yyyy-MM-dd");

export const Booking = () => {
  const [month, setMonth] = useState(new Date());
  const [avail, setAvail] = useState({ slots: [], blocked_days: [], booked: {}, today: fmt(new Date()) });
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const loadAvailability = useCallback(async (m) => {
    try {
      const { data } = await axios.get(`${API}/availability`, { params: { month: format(m, "yyyy-MM") } });
      setAvail(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadAvailability(month);
  }, [month, loadAvailability]);

  const blockedSet = new Set(avail.blocked_days);
  const dateStr = date ? fmt(date) : null;
  const takenSlots = new Set(dateStr ? avail.booked[dateStr] || [] : []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const book = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, { date: dateStr, slot, ...form });
      setConfirmed({ date: dateStr, slot });
      loadAvailability(month);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again.");
      loadAvailability(month);
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#E6DFD5] focus:border-[#8A5A44] outline-none py-3 text-[#2C2A28] placeholder:text-[#5C5854]/50 text-sm transition-colors duration-300";

  return (
    <section id="book" className="py-24 lg:py-32 bg-[#F3EFEA]/50" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-[#8A5A44] mb-4">05 — Book a time</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#2C2A28] max-w-2xl">
            Pick a time for your <em className="italic text-[#8A5A44]">free conversation.</em>
          </h2>
          <p className="text-[#5C5854] leading-relaxed font-light mt-6 max-w-xl">
            Choose a day and time that works for you. In person around Greensboro, or by video
            anywhere in the U.S. All times Eastern.
          </p>
        </Reveal>

        {confirmed ? (
          <Reveal className="mt-16">
            <div className="bg-[#FAF9F6] rounded-3xl p-10 lg:p-14 border border-black/5 max-w-2xl" data-testid="booking-confirmed">
              <div className="w-14 h-14 rounded-full bg-[#8A5A44] flex items-center justify-center">
                <Check size={24} className="text-[#FAF9F6]" />
              </div>
              <h3 className="font-serif-display text-3xl font-medium text-[#2C2A28] mt-8">
                You're on the calendar.
              </h3>
              <p className="text-[#5C5854] font-light leading-relaxed mt-4">
                {format(new Date(`${confirmed.date}T12:00:00`), "EEEE, MMMM d, yyyy")} at {confirmed.slot} (Eastern).
                The Wicks will reach out to confirm the details. Come as you are.
              </p>
              <button
                onClick={() => {
                  setConfirmed(null);
                  setDate(null);
                  setSlot(null);
                  setForm({ name: "", email: "", phone: "", notes: "" });
                }}
                className="mt-8 rounded-full border border-[#C4B299] hover:border-[#8A5A44] text-[#2C2A28] px-7 py-3 text-sm transition-colors duration-300"
                data-testid="booking-book-another"
              >
                Book another time
              </button>
            </div>
          </Reveal>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16">
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="bg-[#FAF9F6] rounded-3xl p-6 lg:p-8 border border-black/5" data-testid="booking-calendar">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    setDate(d);
                    setSlot(null);
                  }}
                  onMonthChange={setMonth}
                  disabled={[
                    { before: new Date(`${avail.today}T00:00:00`) },
                    (d) => blockedSet.has(fmt(d)),
                  ]}
                  className="w-full"
                  classNames={{
                    months: "flex flex-col",
                    table: "w-full border-collapse",
                    head_cell: "text-[#5C5854] w-10 font-normal text-xs uppercase tracking-wider",
                    cell: "p-0.5 text-center",
                    day: "h-10 w-10 rounded-full font-normal text-sm hover:bg-[#E6DFD5] transition-colors duration-200",
                    day_selected: "bg-[#8A5A44] text-[#FAF9F6] hover:bg-[#734A38]",
                    day_today: "border border-[#C4B299]",
                    day_disabled: "text-[#5C5854]/30 hover:bg-transparent line-through",
                    caption_label: "font-serif-display text-lg font-medium",
                  }}
                />
              </div>
            </Reveal>

            <Reveal delay={0.2} className="lg:col-span-7">
              <div className="bg-[#FAF9F6] rounded-3xl p-8 lg:p-10 border border-black/5 h-full">
                {!date ? (
                  <div className="h-full flex flex-col items-center justify-center py-16 text-center">
                    <CalendarHeart size={32} strokeWidth={1.25} className="text-[#C4B299]" />
                    <p className="text-[#5C5854] font-light mt-5" data-testid="booking-pick-date-hint">
                      Pick a day on the calendar to see available times.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="font-serif-display text-2xl font-medium text-[#2C2A28]" data-testid="booking-selected-date">
                      {format(date, "EEEE, MMMM d")}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-6">
                      {avail.slots.map((s) => (
                        <button
                          key={s}
                          disabled={takenSlots.has(s)}
                          onClick={() => setSlot(s)}
                          className={`rounded-full px-6 py-3 text-sm border transition-colors duration-300 ${
                            slot === s
                              ? "bg-[#8A5A44] border-[#8A5A44] text-[#FAF9F6]"
                              : takenSlots.has(s)
                              ? "border-[#E6DFD5] text-[#5C5854]/30 line-through cursor-not-allowed"
                              : "border-[#C4B299]/60 text-[#2C2A28] hover:border-[#8A5A44]"
                          }`}
                          data-testid={`booking-slot-${s.replace(/[:\s]/g, "-")}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>

                    {slot && (
                      <form onSubmit={book} className="mt-10 space-y-6" data-testid="booking-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <input value={form.name} onChange={set("name")} placeholder="Your name *" className={inputClass} data-testid="booking-input-name" />
                          <input value={form.email} onChange={set("email")} type="email" placeholder="Email *" className={inputClass} data-testid="booking-input-email" />
                        </div>
                        <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className={inputClass} data-testid="booking-input-phone" />
                        <input value={form.notes} onChange={set("notes")} placeholder="Anything you'd like us to know? (optional)" className={inputClass} data-testid="booking-input-notes" />
                        <button
                          type="submit"
                          disabled={sending}
                          className="rounded-full bg-[#8A5A44] hover:bg-[#734A38] disabled:opacity-60 text-[#FAF9F6] px-8 py-4 text-sm flex items-center gap-2 transition-colors duration-300"
                          data-testid="booking-submit-button"
                        >
                          {sending && <Loader2 size={16} className="animate-spin" />}
                          {sending ? "Reserving…" : `Reserve ${slot}`}
                        </button>
                      </form>
                    )}
                  </>
                )}
              </div>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
};
